import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { login } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  Divider
} from '@mui/material';
import {
  LockOutlined,
  Business,
  AdminPanelSettings
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 600,
      color: '#1565c0',
    },
    h4: {
      fontWeight: 500,
      color: '#424242',
    },
  },
});

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema: Yup.object({
      username: Yup.string().required('Le nom d\'utilisateur est requis.'),
      password: Yup.string().required('Le mot de passe est requis.')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      const success = await login(values);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Nom d\'utilisateur ou mot de passe incorrect.');
      }
      setLoading(false);
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3), transparent 50%)',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10}>
              <Paper
                elevation={24}
                sx={{
                  p: 8,
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.99)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.15), 0 10px 40px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5, #1976d2)',
                    animation: 'shimmer 3s ease-in-out infinite',
                  },
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' }
                  }
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                  <Avatar sx={{ 
                    bgcolor: 'primary.main', 
                    mx: 'auto', 
                    mb: 3,
                    width: 64,
                    height: 64,
                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)'
                  }}>
                    <LockOutlined sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h2" component="h1" gutterBottom sx={{ 
                    fontWeight: 700, 
                    color: '#1565c0',
                    letterSpacing: '-0.5px'
                  }}>
                    Connexion
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ 
                    mb: 3,
                    fontWeight: 400,
                    color: '#6b7280'
                  }}>
                    Accédez à votre espace de travail
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={formik.handleSubmit}>
                  <TextField
                    fullWidth
                    label="Nom d'utilisateur"
                    variant="outlined"
                    margin="normal"
                    {...formik.getFieldProps('username')}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    helperText={formik.touched.username && formik.errors.username}
                    disabled={loading}
                    sx={{ 
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#6b7280',
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Mot de passe"
                    type="password"
                    variant="outlined"
                    margin="normal"
                    {...formik.getFieldProps('password')}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    disabled={loading}
                    sx={{ 
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#6b7280',
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 2.5,
                      textTransform: 'none',
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      mb: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 50%, #0a237e 100%)',
                        boxShadow: '0 12px 40px rgba(25, 118, 210, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4)',
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={28} color="inherit" />
                    ) : (
                      'Se connecter'
                    )}
                  </Button>

                  <Divider sx={{ my: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Système de Gestion des Ressources Humaines
                    </Typography>
                  </Divider>

                  <Typography variant="body2" color="text.secondary" sx={{ 
                    display: 'block', 
                    textAlign: 'center',
                    lineHeight: 1.6,
                    fontStyle: 'italic'
                  }}>
                    Bienvenue dans votre plateforme RH centralisée. Gérez efficacement vos employés, 
                    leurs congés, les ordres de mission et tous les documents administratifs 
                    en un seul endroit.
                  </Typography>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage;